import { useEffect, useState } from 'react';

import { environment } from '../../../environments/environment';
import type { CompanyMapperSearchResult } from '../../../shared/types/company-result-item';

export interface CompanyRicsIds {
  ricsK10: number | null;
  ricsK50: number | null;
  ricsK400: number | null;
  ricsK400Name: string | null;
  employeeCount: number | null;
}

const EMPTY_RICS: CompanyRicsIds = {
  ricsK10: null,
  ricsK50: null,
  ricsK400: null,
  ricsK400Name: null,
  employeeCount: null,
};

/**
 * Fetches RICS industry mapping for a company via the company mapper search API.
 * Uses the company name to search and matches by RCID.
 */
export const useCompanyRics = (
  companyName: string | null,
  rcid: number | null
): { rics: CompanyRicsIds; isLoading: boolean } => {
  const [rics, setRics] = useState<CompanyRicsIds>(EMPTY_RICS);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!companyName || !rcid) {
      setRics(EMPTY_RICS);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);

    const fetchRics = async () => {
      try {
        const response = await fetch(
          `${environment.COMPANY_MAPPER_URL}/search`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              name: companyName,
              employee_cnt_min: 1,
            }),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          setRics(EMPTY_RICS);
          return;
        }

        const results: CompanyMapperSearchResult[] = await response.json();

        // Find the matching company by RCID
        const match = results.find((r) => r.rcid === rcid);
        if (match?.primary_identifiers) {
          const ids = match.primary_identifiers;
          setRics({
            ricsK10: ids.rics_k10 || null,
            ricsK50: ids.rics_k50 || null,
            ricsK400: ids.rics_k400 || null,
            ricsK400Name: ids.rics_k400_name || null,
            employeeCount: match.raw_emp_count ?? null,
          });
        } else if (results.length > 0 && results[0]?.primary_identifiers) {
          // Fallback: use the first result if RCID doesn't match exactly
          const ids = results[0].primary_identifiers;
          setRics({
            ricsK10: ids.rics_k10 || null,
            ricsK50: ids.rics_k50 || null,
            ricsK400: ids.rics_k400 || null,
            ricsK400Name: ids.rics_k400_name || null,
            employeeCount: results[0].raw_emp_count ?? null,
          });
        } else {
          setRics(EMPTY_RICS);
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setRics(EMPTY_RICS);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchRics();

    return () => controller.abort();
  }, [companyName, rcid]);

  return { rics, isLoading };
};
